import React, { useState, useRef, useEffect, useCallback } from 'react';
import MonthPicker from '../../molecules/profile/components/MonthPicker';
import { showToast } from '../../../utils/toastManager';
import '../../../styles/resume-editor/edit-experience-form.css';

interface Education {
  uid: string;
  institution: string;
  degree: string;
  field_of_study?: string;
  graduation_date?: string | null;
  description?: string;
  grade?: string;
}

interface EditEducationFormProps {
  education: Education;
  onSave: (data: Education) => void;
  onCancel: () => void;
  onDataChange?: (data: Education) => void;
  focusField?: string | null;
  onFocusFieldHandled?: () => void;
}

const degreeOptions = [
  'High School Diploma',
  'Associate Degree',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'Doctoral Degree (Ph.D.)',
  'Professional Degree',
  'Certificate',
  'Other'
];

export const EditEducationForm: React.FC<EditEducationFormProps> = ({
  education,
  onSave,
  onCancel,
  onDataChange,
  focusField,
  onFocusFieldHandled
}) => {
  const [institution, setInstitution] = useState('');
  const [degree, setDegree] = useState('Select degree type');
  const [degreeDropdownOpen, setDegreeDropdownOpen] = useState(false);
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [graduationDate, setGraduationDate] = useState<Date | null>(null);
  const [showGraduationCalendar, setShowGraduationCalendar] = useState(false);
  const [description, setDescription] = useState('');
  const [grade, setGrade] = useState('');
  const [yearOnly, setYearOnly] = useState(false);
  const [hideDate, setHideDate] = useState(false);
  
  const degreeDropdownRef = useRef<HTMLDivElement>(null);
  
  const institutionInputRef = useRef<HTMLInputElement>(null);
  const fieldOfStudyInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const gradeInputRef = useRef<HTMLInputElement>(null);
  const loadedEducationUidRef = useRef<string | null>(null);
  const prevDataRef = useRef<string>('');
  const onDataChangeRef = useRef(onDataChange);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    if (education && education.uid !== loadedEducationUidRef.current) {
      loadedEducationUidRef.current = education.uid;
      setInstitution(education.institution || '');
      setDegree(education.degree || 'Select degree type');
      setFieldOfStudy(education.field_of_study || '');
      setGraduationDate(education.graduation_date ? new Date(education.graduation_date) : null);
      setDescription(education.description || '');
      setGrade(education.grade || '');
      setYearOnly(false);
      setHideDate(false);
      
      if (onDataChangeRef.current) {
        const initialEducation: Education = {
          ...education,
          institution: education.institution || '',
          degree: education.degree || 'Select degree type',
          field_of_study: education.field_of_study || '',
          graduation_date: education.graduation_date || null,
          description: education.description || '',
          grade: education.grade || ''
        };
        onDataChangeRef.current(initialEducation);
      }
    }
  }, [education?.uid]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (degreeDropdownRef.current && !degreeDropdownRef.current.contains(event.target as Node)) {
        setDegreeDropdownOpen(false);
      }
    };

    if (degreeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [degreeDropdownOpen]);

  useEffect(() => {
    if (!focusField) return;

    const timer = setTimeout(() => {
      if (focusField === 'institution' && institutionInputRef.current) {
        institutionInputRef.current.focus();
        institutionInputRef.current.select();
      } else if (focusField === 'field_of_study' && fieldOfStudyInputRef.current) {
        fieldOfStudyInputRef.current.focus();
        fieldOfStudyInputRef.current.select();
      } else if (focusField === 'description' && descriptionTextareaRef.current) {
        descriptionTextareaRef.current.focus();
        descriptionTextareaRef.current.select();
      } else if (focusField === 'grade' && gradeInputRef.current) {
        gradeInputRef.current.focus();
        gradeInputRef.current.select();
      } else if (focusField === 'degree') {
        setDegreeDropdownOpen(true);
      } else if (focusField === 'graduation_date') {
        setShowGraduationCalendar(true);
      }

      onFocusFieldHandled?.();
    }, 150);

    return () => clearTimeout(timer);
  }, [focusField, onFocusFieldHandled]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ graduation date';
    if (yearOnly) {
      return date.getFullYear().toString();
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const createUpdatedEducation = (): Education => {
    const updated: Education = {
      ...education,
      institution,
      degree,
      field_of_study: fieldOfStudy || undefined,
      graduation_date: hideDate ? null : (graduationDate ? graduationDate.toISOString() : null),
      description: description || undefined,
      grade: grade || undefined
    };
    return updated;
  };

  const updatePreviewData = () => {
    if (onDataChangeRef.current) {
      const updated = createUpdatedEducation();
      const currentData = JSON.stringify({
        institution,
        degree,
        fieldOfStudy,
        graduationDate: graduationDate?.toISOString(),
        description,
        grade,
        hideDate,
        yearOnly
      });
      
      if (currentData !== prevDataRef.current) {
        prevDataRef.current = currentData;
        onDataChangeRef.current(updated);
      }
    }
  };

  useEffect(() => {
    const shouldUpdate = education?.uid === 'new' || education?.uid === loadedEducationUidRef.current;
    if ((institution !== '' || degree !== 'Select degree type' || education) && shouldUpdate) {
      updatePreviewData();
    }
  }, [institution, degree, fieldOfStudy, graduationDate, description, grade, hideDate, yearOnly, education?.uid]);

  const handleSave = () => {
    if (!isFormValid) {
      showToast('Please enter institution name and select degree type.', 'error');
      return;
    }
    const updatedEducation = createUpdatedEducation();
    onSave(updatedEducation);
  };

  const isFormValid = institution.trim() !== '' && degree !== 'Select degree type';

  return (
    <div className="edit-experience-form">
      <div className="edit-experience-form-content">
        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Institution</span>
            <span style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'normal' }}>Required</span>
          </label>
          <input
            ref={institutionInputRef}
            type="text"
            className="edit-experience-form-input"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="Enter institution name"
          />
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Degree</span>
            <span style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'normal' }}>Required</span>
          </label>
          <div className="edit-experience-form-input-wrapper" ref={degreeDropdownRef}>
            <button
              type="button"
              className="edit-experience-form-input"
              onClick={() => setDegreeDropdownOpen(!degreeDropdownOpen)}
              style={{ textAlign: 'left', cursor: 'pointer' }}
            >
              {degree}
            </button>
            {degreeDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
                {degreeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setDegree(option);
                      setDegreeDropdownOpen(false);
                    }}
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      textAlign: 'left', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#374151',
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Field of Study</label>
          <input
            ref={fieldOfStudyInputRef}
            type="text"
            className="edit-experience-form-input"
            value={fieldOfStudy}
            onChange={(e) => setFieldOfStudy(e.target.value)}
            placeholder="Enter field of study"
          />
        </div>

        <div className="edit-experience-form-field">
          <div className="edit-experience-form-date-wrapper">
            <label className="edit-experience-form-label">Graduation Date</label>
            <button
              type="button"
              className="edit-experience-form-date-btn"
              onClick={() => {
                setShowGraduationCalendar(!showGraduationCalendar);
              }}
              disabled={hideDate}
            >
              {formatDate(graduationDate)}
            </button>
            {showGraduationCalendar && (
              <div className="edit-experience-form-month-picker-wrapper">
                <MonthPicker
                  selectedDate={graduationDate}
                  onSelect={(date) => {
                    setGraduationDate(date);
                    setShowGraduationCalendar(false);
                  }}
                  onClose={() => setShowGraduationCalendar(false)}
                />
              </div>
            )}
          </div>
          <div className="edit-experience-form-date-toggles">
            <label className="edit-experience-form-toggle-label">
              <span>Year only</span>
              <div
                className={`edit-experience-form-toggle-switch ${yearOnly ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => {
                  setYearOnly(!yearOnly);
                  updatePreviewData();
                }}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
            <label className="edit-experience-form-toggle-label">
              <span>Hide</span>
              <div
                className={`edit-experience-form-toggle-switch ${hideDate ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => {
                  setHideDate(!hideDate);
                  updatePreviewData();
                }}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
          </div>
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Description</label>
          <textarea
            ref={descriptionTextareaRef}
            className="edit-experience-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            rows={3}
          />
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Grade</label>
          <input
            ref={gradeInputRef}
            type="text"
            className="edit-experience-form-input"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            placeholder="Enter grade (e.g., 3.8/4.0)"
          />
        </div>
      </div>

      <div className="edit-experience-form-footer">
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--update"
          onClick={handleSave}
          disabled={!isFormValid}
        >
          {education.uid === 'new' ? 'Add' : 'Update'}
        </button>
      </div>
    </div>
  );
};

